<template>
  <v-card class="solid-mesh-panel" color="panel_sub">
    <v-card-text class="pa-2">
      <v-row dense class="details-wrapper">
        <v-col cols="12">
          <v-card class="solid-mesh-detail-inner" :class="{ 'solid-mesh-detail-view': !isEditing }" color="panel_sub">
            <v-card-text class="pa-2">
              <v-row dense>
                <v-col cols="12">
                  <div class="text-caption text-grey">{{ panelTitle }}</div>
                </v-col>
              </v-row>

              <!-- Shape type row (manual only) -->
              <v-row dense>
                <v-col cols="6">
                  <div class="text-caption">{{ $t("SHAPE_TYPE") }}</div>
                  <div class="text-body-2">{{ shapeTypeLabel }}</div>
                </v-col>
              </v-row>

              <!-- Dimensions (all three X/Y/Z shown) -->
              <v-row dense>
                <v-col cols="12">
                  <div class="text-caption">{{ dimensionsLabel }}</div>
                  <template v-if="isEditing">
                    <div class="field-stack mt-2">
                      <DistanceSettingInput :model-value="rawInputs.dimensionX" :label="dimensionXLabel" active
                        :base-unit="typeConfig.x.baseUnit" :step="dimensionXStep" :max-value="dimensionMax"
                        :min-value="dimensionXStep" :decimal-length="DIMENSION_DECIMALS" density="compact" hide-details
                        variant="outlined" @focus="onDimensionInputFocus" @blur="onDimensionInputBlur()"
                        @update:model-value="onDimensionXChange" />
                      <DistanceSettingInput :model-value="rawInputs.dimensionY" :label="dimensionYLabel" active
                        :base-unit="typeConfig.y.baseUnit" :step="dimensionYStep" :max-value="dimensionMax"
                        :min-value="dimensionYStep" :decimal-length="DIMENSION_DECIMALS" density="compact" hide-details
                        variant="outlined" @focus="onDimensionInputFocus" @blur="onDimensionInputBlur()"
                        @update:model-value="onDimensionYChange" />
                      <DistanceSettingInput :model-value="rawInputs.dimensionZ" :label="dimensionZLabel" active
                        :base-unit="typeConfig.z.baseUnit" :step="dimensionZStep" :max-value="dimensionMax"
                        :min-value="dimensionZStep" :decimal-length="DIMENSION_DECIMALS" density="compact" hide-details
                        variant="outlined" @focus="onDimensionInputFocus" @blur="onDimensionInputBlur()"
                        @update:model-value="onDimensionZChange" />
                    </div>
                  </template>
                  <template v-else>
                    <div class="text-body-2 mt-1">
                      <div>{{ dimensionXLabel }}: {{ formattedDimensionX }}</div>
                      <div>{{ dimensionYLabel }}: {{ formattedDimensionY }}</div>
                      <div>{{ dimensionZLabel }}: {{ formattedDimensionZ }}</div>
                    </div>
                  </template>
                </v-col>
              </v-row>

              <!-- Anchor point header + anchor type toggle -->
              <v-row dense>
                <v-col cols="12" class="d-flex align-center justify-space-between">
                  <div class="text-caption">{{ $t("ANCHOR_POINT") }}</div>
                  <div v-if="isEditing" class="anchor-type-toggle">
                    <v-btn icon variant="text" size="28" class="anchor-type-button"
                      :class="{ 'active-anchor-button': anchorType === 'bottom-nw' }" title="Bottom NW"
                      @click="setAnchorType('bottom-nw')">
                      <span class="anchor-corner-icon corner-nw" aria-hidden="true" />
                    </v-btn>
                    <v-btn icon variant="text" size="28" class="anchor-type-button"
                      :class="{ 'active-anchor-button': anchorType === 'bottom-ne' }" title="Bottom NE"
                      @click="setAnchorType('bottom-ne')">
                      <span class="anchor-corner-icon corner-ne" aria-hidden="true" />
                    </v-btn>
                    <v-btn icon variant="text" size="28" class="anchor-type-button"
                      :class="{ 'active-anchor-button': anchorType === 'bottom-sw' }" title="Bottom SW"
                      @click="setAnchorType('bottom-sw')">
                      <span class="anchor-corner-icon corner-sw" aria-hidden="true" />
                    </v-btn>
                    <v-btn icon variant="text" size="28" class="anchor-type-button"
                      :class="{ 'active-anchor-button': anchorType === 'bottom-se' }" title="Bottom SE"
                      @click="setAnchorType('bottom-se')">
                      <span class="anchor-corner-icon corner-se" aria-hidden="true" />
                    </v-btn>
                    <v-btn icon variant="text" size="28" class="anchor-type-button"
                      :class="{ 'active-anchor-button': anchorType === 'center' }" title="Center"
                      @click="setAnchorType('center')">
                      <v-icon size="18">mdi-crosshairs</v-icon>
                    </v-btn>
                  </div>
                </v-col>
                <template v-if="!isEditing">
                  <v-col cols="12">
                    <div class="text-body-2">
                      X: {{ formatCoordinate(localAnchorPoint.x) }}, Y:
                      {{ formatCoordinate(localAnchorPoint.y) }}, Z:
                      {{ formatCoordinate(localAnchorPoint.z) }}
                    </div>
                  </v-col>
                </template>
              </v-row>

              <!-- Anchor X/Y/Z editing -->
              <template v-if="isEditing">
                <v-row dense>
                  <v-col cols="12">
                    <DistanceSettingInput :model-value="rawInputs.anchorX" label="X"
                      :step="anchorPointStep" active :decimal-length="anchorPointDecimals"
                      density="compact" hide-details variant="outlined" class="mb-1"
                      :class="{ 'dragging-locked': isDraggingSolidMesh }" @update:model-value="onAnchorXChange" />
                  </v-col>
                  <v-col cols="12">
                    <DistanceSettingInput :model-value="rawInputs.anchorY" label="Y"
                      :step="anchorPointStep" active :decimal-length="anchorPointDecimals"
                      density="compact" hide-details variant="outlined" class="mb-1"
                      :class="{ 'dragging-locked': isDraggingSolidMesh }" @update:model-value="onAnchorYChange" />
                  </v-col>
                  <v-col cols="12">
                    <DistanceSettingInput :model-value="rawInputs.anchorZ" label="Z"
                      :step="anchorPointStep" active :decimal-length="anchorPointDecimals"
                      density="compact" hide-details variant="outlined" class="mb-1"
                      :class="{ 'dragging-locked': isDraggingSolidMesh }" @update:model-value="onAnchorZChange" />
                  </v-col>
                </v-row>
              </template>

              <!-- Transparency & Color -->
              <template v-if="isEditing">
                <v-row dense>
                  <v-col cols="12">
                    <div class="text-caption">{{ $t("TRANSPARENCY") }}</div>
                    <v-text-field :model-value="rawInputs.transparency" type="number" :min="transparencyConstraint.min"
                      :max="transparencyConstraint.max" :step="transparencyConstraint.step" density="compact"
                      hide-details variant="outlined" suffix="%"
                      @update:model-value="(v) => onTransparencyChangeLocal(v)" @input="onTransparencyInput" />
                  </v-col>
                </v-row>
                <v-row dense>
                  <v-col cols="12">
                    <div class="text-caption">{{ $t("COLOR") }}</div>
                    <div class="d-flex align-center gap-2">
                      <v-menu :close-on-content-click="false" location="bottom start">
                        <template v-slot:activator="{ props: menuProps }">
                          <div class="color-preview mr-2 cursor-pointer" :style="{ backgroundColor: editColorString }"
                            v-bind="menuProps" />
                        </template>
                        <v-card min-width="300">
                          <v-color-picker v-model="pickerColor" mode="rgb" hide-inputs show-swatches
                            @update:model-value="onPickerColorChange" />
                        </v-card>
                      </v-menu>
                      <v-text-field :model-value="rawInputs.colorR" label="R" type="number" :min="colorConstraint.min"
                        :max="colorConstraint.max" :step="colorConstraint.step" density="compact" hide-details
                        variant="outlined" style="max-width: 70px" persistent-placeholder placeholder=""
                        @update:model-value="(v) => onColorRChange(v)" @input="onColorRInput" />
                      <v-text-field :model-value="rawInputs.colorG" label="G" type="number" :min="colorConstraint.min"
                        :max="colorConstraint.max" :step="colorConstraint.step" density="compact" hide-details
                        variant="outlined" style="max-width: 70px" persistent-placeholder placeholder=""
                        @update:model-value="(v) => onColorGChange(v)" @input="onColorGInput" />
                      <v-text-field :model-value="rawInputs.colorB" label="B" type="number" :min="colorConstraint.min"
                        :max="colorConstraint.max" :step="colorConstraint.step" density="compact" hide-details
                        variant="outlined" style="max-width: 70px" persistent-placeholder placeholder=""
                        @update:model-value="(v) => onColorBChange(v)" @input="onColorBInput" />
                    </div>
                  </v-col>
                </v-row>
              </template>
              <template v-else>
                <v-row dense>
                  <v-col cols="3">
                    <div class="text-caption my-2">{{ $t("TRANSPARENCY") }}</div>
                    <div class="text-body-2">
                      {{ Math.round(shapeColor.alpha * 100) }}%
                    </div>
                  </v-col>
                  <v-col cols="9">
                    <div class="text-caption my-2">{{ $t("COLOR") }}</div>
                    <div class="d-flex align-center">
                      <div class="color-preview mr-2" :style="{ backgroundColor: viewColorString }" />
                      <div class="text-body-2">
                        R: {{ viewColorR }}, G: {{ viewColorG }}, B: {{ viewColorB }}
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </template>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <!-- Transform panel (always shown for manual when editing) -->
      <template v-if="isEditing">
        <v-row dense>
          <v-col cols="12">
            <solid-mesh-transform-panel :solidMeshId="solidMeshId" :isEditing="isEditing" />
          </v-col>
        </v-row>
      </template>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { SIMPLE_STRUCTURE_TYPE } from "@/models/objects/solidMesh/types";
import { computed, onBeforeUnmount, reactive, ref, toRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import DistanceSettingInput from "../DistanceSettingInput.vue";
import SolidMeshTransformPanel from "./SolidMeshTransformPanel.vue";
import {
	DIMENSION_DECIMALS,
	formatDimensionValue,
} from "./composables/useSolidMeshDimensionConfig";
import type { TSolidMeshCommonRawInputs } from "./composables/useSolidMeshEdit";
import {
	SIMPLE_STRUCTURE_DIMENSION_MAX,
	SIMPLE_STRUCTURE_EDIT_CONSTRAINTS,
	normalizeSpinnerInput,
	toOptionalNumber,
	useDimensionValidation,
	useInputActivity,
	useSolidMeshEdit,
} from "./composables/useSolidMeshEdit";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
const props = defineProps<{
	solidMeshId: string;
	isEditing: boolean;
}>();

// ---------------------------------------------------------------------------
// Setup
// ---------------------------------------------------------------------------
const store = useStore();
const { t } = useI18n();

const common = useSolidMeshEdit(
	toRef(props, "solidMeshId"),
	toRef(props, "isEditing"),
	store,
	t,
);

const {
	editData,
	localAnchorPoint,
	isUpdatingPosition,
	anchorFetchTimeout,
	solidMesh,
	panelTitle,
	typeConfig,
	convertLength,
	formatCoordinate,
	dimensionXLabel,
	dimensionYLabel,
	dimensionZLabel,
	dimensionXStep,
	dimensionYStep,
	dimensionZStep,
	scaledDimensions,
	formattedDimensionX,
	formattedDimensionY,
	editColorString,
	pickerColor,
	isDraggingSolidMesh,
	anchorType,
	displayLocalZ,
	onTransparencyChange,
	onPickerColorChange: baseOnPickerColorChange,
	onColorChange,
	updateLocalAnchorPointX,
	updateLocalAnchorPointY,
	updateLocalAnchorPointZ,
	fetchLocalAnchorPoint,
	loadCommonEditData,
	syncDimensionsFromViewer,
	getColorString,
	initCommonRawInputs,
} = common;

type TPoint3 = { x: number; y: number; z: number };

const rawInputs = reactive<TSolidMeshCommonRawInputs>({
	transparency: undefined,
	dimensionX: undefined,
	dimensionY: undefined,
	dimensionZ: undefined,
	colorR: undefined,
	colorG: undefined,
	colorB: undefined,
	anchorX: undefined,
	anchorY: undefined,
	anchorZ: undefined,
});

const originalDimension = ref({
	x: 0,
	y: 0,
	z: 0,
});

const {
	isDimensionInputting,
	isDimensionFieldFocused,
	dimensionInputTypingTimeout,
	onDimensionInputFocus,
	onDimensionInputBlur,
} = useInputActivity(500);

useDimensionValidation({
	rawInputs,
	isEditing: toRef(props, "isEditing"),
	structureType: toRef(common, "structureType"),
	store,
});

const isUpdatingDimensions = ref(false);
const dimensionsLabel = computed(() => t("DIMENSIONS"));
const dimensionMax = SIMPLE_STRUCTURE_DIMENSION_MAX;
const anchorPointStep = SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.anchorPoint.step;
const anchorPointDecimals =
	SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.anchorPoint.decimalLength;
const transparencyConstraint = SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.transparency;
const colorConstraint = SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.colorChannel;

const shapeTypeLabel = computed(() => {
	const sm = solidMesh.value as Record<string, unknown> | undefined;
	const config = sm?.shapeConfig as { shapeType?: string } | undefined;
	const type = config?.shapeType || SIMPLE_STRUCTURE_TYPE.MANUAL;
	const map: Record<string, string> = {
		box: t("SHAPE_CUBE"),
		sphere: t("SHAPE_SPHERE"),
		cylinder: t("SHAPE_CYLINDER"),
	};
	if (map[type]) return map[type];
	try {
		const dynamicKey = `STRUCTURE_TYPE_${String(type).toUpperCase().replace(/-/g, "_")}`;
		const translated = t(dynamicKey);
		if (translated && translated !== dynamicKey) return translated;
	} catch {
		// ignore
	}
	return type;
});

const formattedDimensionZ = computed(() => {
	const cfg = typeConfig.value;
	const val = Number(scaledDimensions.value.z);
	return formatDimensionValue(val, cfg.z, convertLength);
});

// View-mode color
const shapeColor = computed(() => {
	const sm = solidMesh.value as Record<string, unknown> | undefined;
	const config = sm?.shapeConfig as
		| { color: { red: number; green: number; blue: number; alpha: number } }
		| undefined;
	return config?.color ?? { red: 0, green: 0, blue: 0, alpha: 0 };
});
const viewColorString = computed(() => getColorString(shapeColor.value));
const viewColorR = computed(() => Math.round(shapeColor.value.red * 255));
const viewColorG = computed(() => Math.round(shapeColor.value.green * 255));
const viewColorB = computed(() => Math.round(shapeColor.value.blue * 255));

// ---------------------------------------------------------------------------
// Methods
// ---------------------------------------------------------------------------
function getSolidMesh() {
	return solidMesh.value as Record<string, unknown>;
}

async function loadEditData() {
	await loadCommonEditData();
	initCommonRawInputs(rawInputs);
	const config = getSolidMesh()?.shapeConfig as
		| { dimensions?: TPoint3 }
		| undefined;
	const dimensions = config?.dimensions;
	if (!dimensions) {
		originalDimension.value = {
			x: editData.dimensions.x,
			y: editData.dimensions.y,
			z: editData.dimensions.z,
		};
		return;
	}
	originalDimension.value = {
		x: dimensions.x,
		y: dimensions.y,
		z: dimensions.z,
	};
}

// --- rawInputs change handlers ---
// DistanceSettingInput emits values in the axis base unit. Its clamp and decimal
// limit are applied to the displayed value, so on a feet site the same numeric
// limits are read as feet by design. These handlers only store and propagate.
function onDimensionXChange(val: number | undefined | null) {
	rawInputs.dimensionX = val ?? undefined;
	if (val === undefined || val === null) return;
	editData.dimensions.x = val;
	onDimensionsChange();
}

function onDimensionYChange(val: number | undefined | null) {
	rawInputs.dimensionY = val ?? undefined;
	if (val === undefined || val === null) return;
	editData.dimensions.y = val;
	onDimensionsChange();
}

function onDimensionZChange(val: number | undefined | null) {
	rawInputs.dimensionZ = val ?? undefined;
	if (val === undefined || val === null) return;
	editData.dimensions.z = val;
	onDimensionsChange();
}

function onTransparencyChangeLocal(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.transparency = normalized;
	if (normalized === undefined) return;
	const constrained = normalizeSpinnerInput(normalized, transparencyConstraint);
	rawInputs.transparency = constrained;
	editData.transparency = constrained;
	onTransparencyChange();
}

function onTransparencyInput(event) {
	const target = event.target;
	if (!target) return;
	if (target.value === "") {
		rawInputs.transparency = undefined;
		return;
	}
	const parsed = target.valueAsNumber;
	if (!Number.isFinite(parsed)) return;
	const normalized = normalizeSpinnerInput(parsed, transparencyConstraint);
	target.valueAsNumber = normalized;
	onTransparencyChangeLocal(normalized);
}

function onColorRChange(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.colorR = normalized;
	if (normalized === undefined) return;
	const constrained = normalizeSpinnerInput(normalized, colorConstraint);
	rawInputs.colorR = constrained;
	editData.color.red = constrained;
	onColorChange();
}

function onColorRInput(event) {
	const target = event.target;
	if (!target) return;
	if (target.value === "") {
		rawInputs.colorR = undefined;
		return;
	}
	const parsed = target.valueAsNumber;
	if (!Number.isFinite(parsed)) return;
	const normalized = normalizeSpinnerInput(parsed, colorConstraint);
	target.valueAsNumber = normalized;
	onColorRChange(normalized);
}

function onColorGChange(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.colorG = normalized;
	if (normalized === undefined) return;
	const constrained = normalizeSpinnerInput(normalized, colorConstraint);
	rawInputs.colorG = constrained;
	editData.color.green = constrained;
	onColorChange();
}

function onColorGInput(event) {
	const target = event.target;
	if (!target) return;
	if (target.value === "") {
		rawInputs.colorG = undefined;
		return;
	}
	const parsed = target.valueAsNumber;
	if (!Number.isFinite(parsed)) return;
	const normalized = normalizeSpinnerInput(parsed, colorConstraint);
	target.valueAsNumber = normalized;
	onColorGChange(normalized);
}

function onColorBChange(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.colorB = normalized;
	if (normalized === undefined) return;
	const constrained = normalizeSpinnerInput(normalized, colorConstraint);
	rawInputs.colorB = constrained;
	editData.color.blue = constrained;
	onColorChange();
}

function onColorBInput(event) {
	const target = event.target;
	if (!target) return;
	if (target.value === "") {
		rawInputs.colorB = undefined;
		return;
	}
	const parsed = target.valueAsNumber;
	if (!Number.isFinite(parsed)) return;
	const normalized = normalizeSpinnerInput(parsed, colorConstraint);
	target.valueAsNumber = normalized;
	onColorBChange(normalized);
}

/** Shadow composable's onPickerColorChange to also sync rawInputs. */
function onPickerColorChange(val: unknown) {
	baseOnPickerColorChange(val);
	rawInputs.colorR = editData.color.red;
	rawInputs.colorG = editData.color.green;
	rawInputs.colorB = editData.color.blue;
}

function onAnchorXChange(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.anchorX = normalized;
	if (normalized === undefined) return;
	updateLocalAnchorPointX(normalized);
}

function onAnchorYChange(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.anchorY = normalized;
	if (normalized === undefined) return;
	updateLocalAnchorPointY(normalized);
}

function onAnchorZChange(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.anchorZ = normalized;
	if (normalized === undefined) return;
	updateLocalAnchorPointZ(normalized);
}

async function setAnchorType(newAnchorType: string) {
	const sm = getSolidMesh();
	if (isDraggingSolidMesh.value || !sm) return;
	if (anchorType.value === newAnchorType) return;
	await (sm.setAnchorType as (t: string) => Promise<void>)(newAnchorType);
	await syncCoordinatesFromViewer();
}

/**
 * 編集後の寸法を viewer へ書き戻す。
 * scale を積み増さず、読み込み時の元寸法を基準に寸法そのものを更新するため、
 * 大きな値を入れても scale が発散しない (D3D-799)。
 */
function onDimensionsChange() {
	const sm = getSolidMesh();
	if (!sm) return;
	if (
		!(
			editData.dimensions.x > 0 &&
			editData.dimensions.y > 0 &&
			editData.dimensions.z > 0
		)
	)
		return;
	isUpdatingDimensions.value = true;
	// originalDimension は viewer 由来の m 値。editData は軸の baseUnit なので m に揃える。
	const cfg = typeConfig.value;
	const editedMeters = {
		x: convertLength(editData.dimensions.x, cfg.x.baseUnit, "m"),
		y: convertLength(editData.dimensions.y, cfg.y.baseUnit, "m"),
		z: convertLength(editData.dimensions.z, cfg.z.baseUnit, "m"),
	};
	try {
		(sm.setDimensions as (org: TPoint3, dist: TPoint3) => void)(
			originalDimension.value,
			editedMeters,
		);
	} finally {
		setTimeout(() => {
			isUpdatingDimensions.value = false;
		}, 0);
	}
}

async function syncCoordinatesFromViewer() {
	const sm = getSolidMesh();
	if (!sm?.isLoaded || !props.isEditing) return;
	await fetchLocalAnchorPoint();
	editData.localAnchorPoint = {
		x: localAnchorPoint.x,
		y: localAnchorPoint.y,
		z: localAnchorPoint.z,
	};
}

// ---------------------------------------------------------------------------
// Watchers
// ---------------------------------------------------------------------------

// solidMesh.isLoaded
watch(
	() =>
		(solidMesh.value as Record<string, unknown> | undefined)?.isLoaded as
			| boolean
			| undefined,
	async (isLoaded) => {
		if (isLoaded) {
			await fetchLocalAnchorPoint();
			if (props.isEditing) {
				await loadEditData();
			}
		}
	},
	{ immediate: true },
);

// isEditing
watch(
	() => props.isEditing,
	async (newVal) => {
		if (newVal) {
			await loadEditData();
		}
	},
	{ immediate: true },
);

// wgs84Center
watch(
	() => {
		const sm = solidMesh.value as Record<string, unknown> | undefined;
		const config = sm?.shapeConfig as { wgs84Center?: TPoint3 } | undefined;
		return config?.wgs84Center;
	},
	() => {
		if (props.isEditing && !isUpdatingPosition.value) {
			if (anchorFetchTimeout.value) clearTimeout(anchorFetchTimeout.value);
			anchorFetchTimeout.value = setTimeout(() => {
				syncCoordinatesFromViewer();
			}, 0);
		}
	},
	{ deep: true },
);

// scale
watch(
	() => {
		const sm = solidMesh.value as Record<string, unknown> | undefined;
		const config = sm?.shapeConfig as { scale?: TPoint3 } | undefined;
		return config?.scale;
	},
	() => {
		if (
			props.isEditing &&
			!isUpdatingDimensions.value &&
			!isDimensionInputting.value &&
			!isDimensionFieldFocused.value
		) {
			syncDimensionsFromViewer();
			// Both editData and rawInputs hold axis base-unit values.
			// DistanceSettingInput owns the conversion to the site display unit.
			rawInputs.dimensionX = editData.dimensions.x;
			rawInputs.dimensionY = editData.dimensions.y;
			rawInputs.dimensionZ = editData.dimensions.z;
		}
	},
	{ deep: true },
);

onBeforeUnmount(() => {
	if (dimensionInputTypingTimeout.value) {
		clearTimeout(dimensionInputTypingTimeout.value);
		dimensionInputTypingTimeout.value = null;
	}
});

// Sync anchorZ from displayLocalZ (handles drag preDragLocalZ).
// Kept in metres: DistanceSettingInput converts for display.
watch(displayLocalZ, (val) => {
	rawInputs.anchorZ = val;
});

// Sync anchorX/Y when coordinates update from viewer
watch(
	() => editData.localAnchorPoint,
	(anchor) => {
		if (!props.isEditing) return;
		rawInputs.anchorX = anchor.x;
		rawInputs.anchorY = anchor.y;
	},
	{ deep: true },
);
</script>

<style scoped>
.solid-mesh-panel {
  margin-bottom: 8px;
  background-color: rgb(var(--v-theme-panel_mian));
  opacity: 1;
}

.solid-mesh-panel :deep(.v-card-text) {
  background-color: rgb(var(--v-theme-panel_main));
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.solid-mesh-detail-view :deep(.v-card-text) {
  background-color: rgb(var(--v-theme-surface));
}

.color-preview {
  width: 20px;
  height: 20px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.cursor-pointer {
  cursor: pointer;
}

.text-caption {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.text-body-2 {
  color: white;
}

.gap-2 {
  gap: 8px;
}

.field-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.field-stack :deep(.v-input),
.field-stack :deep(.v-text-field) {
  width: 100%;
}

.anchor-type-toggle {
  display: flex;
  gap: 6px;
}

.anchor-type-button {
  min-width: 28px;
  width: 28px;
  height: 28px;
  background-color: rgba(50, 50, 50, 0.7);
  border: 1px solid rgba(100, 100, 100, 0.9);
  color: white;
}

.anchor-type-button.active-anchor-button {
  background-color: #1976d2;
}

.anchor-type-button:disabled {
  opacity: 0.4;
}

.anchor-corner-icon {
  display: inline-block;
  position: relative;
  width: 14px;
  height: 14px;
}

.anchor-corner-icon::before,
.anchor-corner-icon::after {
  content: "";
  position: absolute;
  background-color: currentColor;
}

.corner-nw::before {
  top: 0;
  left: 0;
  width: 2px;
  height: 100%;
}

.corner-nw::after {
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
}

.corner-ne::before {
  top: 0;
  right: 0;
  width: 2px;
  height: 100%;
}

.corner-ne::after {
  top: 0;
  right: 0;
  width: 100%;
  height: 2px;
}

.corner-sw::before {
  bottom: 0;
  left: 0;
  width: 2px;
  height: 100%;
}

.corner-sw::after {
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
}

.corner-se::before {
  bottom: 0;
  right: 0;
  width: 2px;
  height: 100%;
}

.corner-se::after {
  bottom: 0;
  right: 0;
  width: 100%;
  height: 2px;
}

.v-text-field :deep(.v-field) {
  font-size: 12px;
}

.v-text-field :deep(.v-field__input) {
  padding: 8px;
  min-height: 28px;
}

.dragging-locked :deep(.v-field) {
  background-color: rgba(100, 100, 100, 0);
}
</style>
